import React, { useCallback, useEffect, useMemo, useState } from "react";
import Topbar from "@/components/Topbar";
import { api, boutiqueOf, roleLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import { Skeleton } from "@/components/Skeleton";
import EventDialog from "@/components/calendar/EventDialog";
import EventDetailDialog from "@/components/calendar/EventDetailDialog";
import { Calendar as CalIcon, Plus, ChevronLeft, ChevronRight, MapPin, Users2, AlertCircle } from "lucide-react";

// ─── date helpers ────────────────────────────────────────────────────────────
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function fmtDayISO(d) { return d.toISOString().slice(0, 10); }
function monthLabel(d) {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
function buildGrid(monthDate) {
  const first = startOfMonth(monthDate);
  const startWeekday = first.getDay(); // 0=Sun
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startWeekday);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}

// ─── main page ───────────────────────────────────────────────────────────────
export default function CalendarPage({ embedded = false }) {
  const { user } = useAuth();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(new Date());
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState(null);
  const [directory, setDirectory] = useState({ users: [], boutiques: [], roles: [] });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // event obj if editing
  const [viewing, setViewing] = useState(null); // event obj if viewing detail

  const isFrontLine = user?.role === "front_desk" || user?.role === "massage_therapist";

  const load = useCallback(async () => {
    try {
      const start = new Date(month);
      start.setDate(start.getDate() - 7);
      const end = new Date(month);
      end.setMonth(end.getMonth() + 1);
      end.setDate(end.getDate() + 7);
      const res = await api.get("/calendar/events", {
        params: { start: start.toISOString(), end: end.toISOString() },
      });
      setEvents(res.data.events || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load calendar");
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/calendar/directory")
      .then((r) => setDirectory(r.data))
      .catch(() => {});
  }, []);

  const eventsByDay = useMemo(() => {
    const map = {};
    (events || []).forEach((ev) => {
      const d = new Date(ev.start_at);
      const key = fmtDayISO(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
      map[key] = map[key] || [];
      map[key].push(ev);
    });
    return map;
  }, [events]);

  const selectedKey = fmtDayISO(new Date(selected.getFullYear(), selected.getMonth(), selected.getDate()));
  const dayEvents = (eventsByDay[selectedKey] || []).sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
  const grid = buildGrid(month);
  const today = new Date();

  const upcomingRsvps = useMemo(() => {
    return (events || [])
      .filter((e) => e.is_required && !e.my_rsvp && new Date(e.end_at) > new Date())
      .sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""))
      .slice(0, 3);
  }, [events]);

  const onSaved = (savedEvent) => {
    setCreating(false);
    setEditing(null);
    load();
  };
  const onDeleted = () => {
    setViewing(null);
    load();
  };
  const onRsvpChanged = (updated) => {
    // refresh in-place
    setViewing(updated);
    setEvents((prev) => (prev || []).map((e) => (e.id === updated.id ? updated : e)));
  };

  return (
    <div>
      {!embedded && <Topbar title="Calendar" />}
      <div className={`${embedded ? "px-0" : "px-4 sm:px-10 stagger-fade"} pb-12 sm:pb-16`} data-testid="calendar-page">
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <div className="label-eyebrow mb-1">Team · shared calendar</div>
            <h2 className={`font-serif-display text-2xl ${embedded ? "text-inherit" : "text-stone-900"}`}>Sharable events</h2>
            <p className={`text-sm ${embedded ? "text-inherit opacity-80" : "text-stone-500"} mt-2 max-w-2xl leading-relaxed`}>
              Schedule cross-boutique and in-boutique events. Decide who needs to know
              and who must attend — required attendees get an RSVP prompt.
            </p>
          </div>
          {!isFrontLine && (
            <button
              onClick={() => setCreating(true)}
              data-testid="btn-new-event"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 self-start"
            >
              <Plus size={14} strokeWidth={1.75} />
              New event
            </button>
          )}
        </header>

        {err && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 flex items-center gap-2 mb-4" data-testid="calendar-error">
            <AlertCircle size={14} /> {err}
          </div>
        )}

        {upcomingRsvps.length > 0 && (
          <section className="mb-5 rounded-2xl border border-amber-200 bg-amber-50/70 p-4" data-testid="calendar-rsvp-nudges">
            <div className="label-eyebrow text-amber-900 mb-2">You must attend</div>
            <div className="space-y-2">
              {upcomingRsvps.map((ev) => (
                <button
                  key={ev.id}
                  onClick={() => setViewing(ev)}
                  data-testid={`calendar-rsvp-nudge-${ev.id}`}
                  className="w-full text-left flex items-center justify-between gap-3 rounded-xl bg-white/70 hover:bg-white px-3 py-2 border border-amber-100"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-stone-900 truncate">{ev.title}</div>
                    <div className="text-[11px] text-stone-500">
                      {new Date(ev.start_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      {ev.location ? ` · ${ev.location}` : ""}
                    </div>
                  </div>
                  <span className="text-[11px] text-amber-900 font-medium shrink-0">RSVP →</span>
                </button>
              ))}
            </div>
          </section>
        )}

        <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
          {/* ── Month grid ────────────────────────────────────────────── */}
          <section className="bg-white subtle-border rounded-3xl card-shadow overflow-hidden" data-testid="calendar-grid">
            <header className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMonth(addMonths(month, -1))}
                  data-testid="btn-month-prev"
                  className="p-1.5 rounded-full hover:bg-stone-100 text-stone-600"
                  aria-label="Previous month"
                ><ChevronLeft size={16} strokeWidth={1.5} /></button>
                <div className="font-serif-display text-base sm:text-lg text-stone-900 min-w-[7rem] sm:min-w-[10rem] text-center" data-testid="calendar-month-label">
                  {monthLabel(month)}
                </div>
                <button
                  onClick={() => setMonth(addMonths(month, 1))}
                  data-testid="btn-month-next"
                  className="p-1.5 rounded-full hover:bg-stone-100 text-stone-600"
                  aria-label="Next month"
                ><ChevronRight size={16} strokeWidth={1.5} /></button>
              </div>
              <button
                onClick={() => { setMonth(startOfMonth(new Date())); setSelected(new Date()); }}
                data-testid="btn-today"
                className="text-xs text-stone-500 hover:text-stone-900 px-3 py-1 rounded-full hover:bg-stone-100"
              >Today</button>
            </header>

            {!events && <div className="p-6 space-y-3">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-64 w-full rounded-2xl" />
            </div>}

            {events && (
              <>
                <div className="grid grid-cols-7 border-b border-stone-100 text-[10px] uppercase tracking-wider text-stone-400">
                  {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                    <div key={d} className="px-2 py-2 text-center">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {grid.map((d, i) => {
                    const key = fmtDayISO(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
                    const dayEvs = eventsByDay[key] || [];
                    const inMonth = d.getMonth() === month.getMonth();
                    const isToday = sameDay(d, today);
                    const isSelected = sameDay(d, selected);
                    return (
                      <button
                        key={i}
                        onClick={() => setSelected(d)}
                        data-testid={`calendar-day-${key}`}
                        className={`min-h-[84px] px-2 py-1.5 border-t border-r border-stone-50 text-left transition-colors ${
                          isSelected ? "bg-stone-100" : "hover:bg-stone-50/60"
                        } ${!inMonth ? "opacity-40" : ""} ${(i % 7) === 6 ? "border-r-0" : ""}`}
                      >
                        <div className={`text-[11px] mb-1 ${isToday ? "text-white bg-stone-900 rounded-full w-5 h-5 inline-flex items-center justify-center" : "text-stone-500"}`}>
                          {d.getDate()}
                        </div>
                        <div className="space-y-0.5">
                          {dayEvs.slice(0, 2).map((ev) => (
                            <div
                              key={ev.id}
                              onClick={(e) => { e.stopPropagation(); setViewing(ev); }}
                              data-testid={`calendar-chip-${ev.id}`}
                              className={`truncate text-[10.5px] rounded px-1.5 py-0.5 border cursor-pointer ${
                                ev.is_required
                                  ? "bg-amber-50 border-amber-200 text-amber-900"
                                  : ev.boutique_code
                                    ? "bg-stone-50 border-stone-200 text-stone-700"
                                    : "bg-white border-stone-200 text-stone-700"
                              }`}
                              title={ev.title}
                            >
                              {ev.title}
                            </div>
                          ))}
                          {dayEvs.length > 2 && (
                            <div className="text-[10px] text-stone-400 px-1">+{dayEvs.length - 2} more</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          {/* ── Day agenda ────────────────────────────────────────────── */}
          <aside className="bg-white subtle-border rounded-3xl card-shadow p-5" data-testid="calendar-day-panel">
            <div className="label-eyebrow mb-1">Agenda</div>
            <h3 className="font-serif-display text-lg text-stone-900 mb-4">
              {selected.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </h3>
            {events === null ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full rounded-2xl" />
                <Skeleton className="h-16 w-full rounded-2xl" />
              </div>
            ) : dayEvents.length === 0 ? (
              <div className="text-center py-10 text-sm text-stone-400 italic" data-testid="calendar-day-empty">
                <CalIcon size={22} className="mx-auto mb-2 text-stone-300" strokeWidth={1.25} />
                Nothing on the calendar this day.
              </div>
            ) : (
              <div className="space-y-3">
                {dayEvents.map((ev) => (
                  <button
                    key={ev.id}
                    onClick={() => setViewing(ev)}
                    data-testid={`calendar-agenda-${ev.id}`}
                    className="w-full text-left rounded-2xl border border-stone-100 hover:border-stone-300 hover:bg-stone-50 p-3 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[11px] text-stone-500 uppercase tracking-wider">
                        {ev.all_day ? "All day" : `${fmtTime(ev.start_at)} – ${fmtTime(ev.end_at)}`}
                      </div>
                      {ev.is_required && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 font-medium">
                          You must attend
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-stone-900 mt-1">{ev.title}</div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-stone-500">
                      {ev.location && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {ev.location}</span>}
                      {ev.boutique_code && (
                        <span className="inline-flex items-center gap-1">
                          <span className={`w-1.5 h-1.5 rounded-full ${boutiqueOf(ev.boutique_code).dot}`} />
                          {ev.boutique_code}
                        </span>
                      )}
                      {(ev.required_attendees || []).length > 0 && (
                        <span className="inline-flex items-center gap-1"><Users2 size={10} /> {ev.required_attendees.length} required</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </aside>
        </div>
      </div>

      {creating && (
        <EventDialog
          mode="create"
          directory={directory}
          defaultDate={selected}
          onClose={() => setCreating(false)}
          onSaved={onSaved}
        />
      )}
      {editing && (
        <EventDialog
          mode="edit"
          event={editing}
          directory={directory}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
      {viewing && (
        <EventDetailDialog
          event={viewing}
          directory={directory}
          onClose={() => setViewing(null)}
          onEdit={() => { setEditing(viewing); setViewing(null); }}
          onDeleted={onDeleted}
          onRsvpChanged={onRsvpChanged}
        />
      )}
    </div>
  );
}
